import React, { useState } from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { FiPhone, FiMail, FiMapPin, FiClock, FiSend, FiCheckCircle, FiAlertCircle } from 'react-icons/fi'

const PageContainer = styled.div`
  padding-top: 80px;
`

const HeroSection = styled.section`
  background: var(--gradient-secondary);
  color: white;
  padding: 6rem 0 4rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    left: -20%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 100px;
    background: var(--color-bg-card);
    clip-path: polygon(0 60%, 100% 0, 100% 100%, 0 100%);
  }
`

const ContactSection = styled.section`
  padding: 6rem 0;
  background: linear-gradient(180deg, var(--color-bg-card) 0%, var(--color-bg-subtle) 100%);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 30%;
    right: -10%;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(37, 99, 235, 0.05) 0%, transparent 70%);
    border-radius: 50%;
    z-index: 0;
  }
`

const ContactContent = styled.div`
  max-width: var(--container-max-width);
  margin: 0 auto;
  padding: 0 var(--container-padding);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`

const ContactInfo = styled.div`
  h2 {
    font-size: 2rem;
    margin-bottom: 1.5rem;
    color: var(--color-text);
  }

  p {
    font-size: 1.125rem;
    margin-bottom: 2rem;
    color: var(--color-text-light);
    line-height: 1.7;
  }
`

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.75rem;
  background: var(--color-bg-card);
  border-radius: var(--border-radius-lg);
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(37, 99, 235, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: var(--gradient-accent);
    transform: scaleY(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(37, 99, 235, 0.15);
    border-color: rgba(37, 99, 235, 0.2);

    &::before {
      transform: scaleY(1);
    }
  }

  svg {
    color: var(--color-primary);
    font-size: 1.75rem;
    flex-shrink: 0;
    background: rgba(37, 99, 235, 0.1);
    padding: 0.5rem;
    border-radius: 50%;
  }

  .contact-details {
    h4 {
      margin: 0 0 0.25rem 0;
      color: var(--color-text);
      font-weight: 600;
      font-size: 1.125rem;
    }

    p {
      margin: 0;
      color: var(--color-text-light);
      font-size: 0.95rem;
    }

    a {
      color: var(--color-primary);
      text-decoration: none;
      font-weight: 600;
      font-size: 1.125rem;

      &:hover {
        color: var(--color-secondary);
      }
    }
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
    gap: 0.75rem;

    svg {
      font-size: 1.5rem;
      padding: 0.4rem;
    }

    .contact-details {
      h4 {
        font-size: 1rem;
      }

      a {
        font-size: 1rem;
      }

      p {
        font-size: 0.875rem;
      }
    }
  }
`

const ContactForm = styled(motion.form)`
  background: var(--color-bg-card);
  padding: 3rem;
  border-radius: var(--border-radius-xl);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: var(--gradient-accent);
  }

  h2 {
    margin-bottom: 1.5rem;
    color: var(--color-text);
  }

  p {
    margin-bottom: 2rem;
    color: var(--color-text-light);
  }

  @media (max-width: 768px) {
    padding: 2rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
  }
`

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
    color: var(--color-text);
  }

  input,
  textarea {
    width: 100%;
    padding: 0.875rem 1.25rem;
    border: 2px solid var(--color-border);
    border-radius: var(--border-radius);
    font-size: 1rem;
    transition: all 0.3s ease;
    background: var(--color-bg-subtle);

    &:focus {
      outline: none;
      border-color: var(--color-primary);
      background: var(--color-bg-card);
      box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1);
    }

    &::placeholder {
      color: var(--color-text-light);
    }
  }

  textarea {
    min-height: 120px;
    resize: vertical;
  }
`

const SubmitButton = styled.button`
  width: 100%;
  padding: 1.125rem 2rem;
  background: var(--gradient-accent);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 4px 15px rgba(37, 99, 235, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(37, 99, 235, 0.4);

    &::before {
      width: 300px;
      height: 300px;
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const StatusMessage = styled.div<{ $type: 'success' | 'error' }>`
  padding: 1rem;
  border-radius: var(--border-radius);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: ${props => props.$type === 'success' ? '#f0f9ff' : '#fef2f2'};
  border: 1px solid ${props => props.$type === 'success' ? '#bae6fd' : '#fecaca'};
  color: ${props => props.$type === 'success' ? '#0369a1' : '#dc2626'};

  svg {
    flex-shrink: 0;
  }
`

interface FormData {
  name: string
  email: string
  company: string
  phone: string
  message: string
}

const ContactPage: React.FC = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 })
  const [contactRef, contactInView] = useInView({ triggerOnce: true, threshold: 0.1 })
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  })
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        setSubmitStatus('success')
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: ''
        })
      } else {
        setSubmitStatus('error')
      }
    } catch (error) {
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const contactItems = [
    {
      icon: <FiPhone />,
      title: 'Telefon',
      content: (
        <>
          <a href="tel:510184702">510 184 702</a>
          <p>Pn-Pt 8:00-17:00</p>
        </>
      )
    },
    {
      icon: <FiMail />,
      title: 'Email',
      content: (
        <>
          <a href="mailto:kontakt@fachfix.pl">kontakt@fachfix.pl</a>
          <p>Odpowiadamy w ciągu 24h</p>
        </>
      )
    },
    {
      icon: <FiMapPin />,
      title: 'Obszar działania',
      content: (
        <>
          <p>Trójmiasto i okolice</p>
          <p>Dojazd do klienta</p>
        </>
      )
    },
    {
      icon: <FiClock />,
      title: 'Godziny pracy',
      content: (
        <>
          <p>Poniedziałek - Piątek</p>
          <p>8:00 - 17:00</p>
        </>
      )
    }
  ]

  return (
    <PageContainer>
      <HeroSection ref={heroRef}>
        <div className="container">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            Kontakt
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{ fontSize: '1.25rem', opacity: 0.9 }}
          >
            Skontaktuj się z nami i otrzymaj bezpłatną wycenę
          </motion.p>
        </div>
      </HeroSection>

      <ContactSection ref={contactRef}>
        <ContactContent>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={contactInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <ContactInfo>
              <h2>Skontaktuj się z nami</h2>
              <p>
                            Jesteśmy dostępni dla naszych klientów w godzinach pracy. 
            Skontaktuj się z nami w dogodny dla Ciebie sposób.
              </p>
            </ContactInfo>
            
            <ContactList>
              {contactItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  animate={contactInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.2 + index * 0.1 }}
                >
                  <ContactItem>
                    {item.icon}
                    <div className="contact-details">
                      <h4>{item.title}</h4>
                      {item.content}
                    </div>
                  </ContactItem>
                </motion.div>
              ))}
            </ContactList>
          </motion.div>

          <ContactForm
            initial={{ opacity: 0, x: 30 }}
            animate={contactInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            onSubmit={handleSubmit}
          >
            <h2>Wyślij zapytanie</h2>
            <p>Wypełnij formularz, a skontaktujemy się z Tobą w ciągu 24 godzin.</p>
            
            {submitStatus === 'success' && (
              <StatusMessage $type="success">
                <FiCheckCircle />
                Dziękujemy za wiadomość! Skontaktujemy się z Tobą wkrótce.
              </StatusMessage>
            )}
            
            {submitStatus === 'error' && (
              <StatusMessage $type="error">
                <FiAlertCircle />
                Wystąpił błąd podczas wysyłania wiadomości. Spróbuj ponownie lub zadzwoń do nas.
              </StatusMessage>
            )}

            <FormGroup>
              <label htmlFor="name">Imię i nazwisko *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Wprowadź swoje imię i nazwisko"
                required
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="twoj@email.com"
                required
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="company">Firma *</label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                placeholder="Nazwa Twojej firmy"
                required
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="phone">Telefon</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="123 456 789"
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="message">Wiadomość *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Opisz swoje potrzeby lub problem techniczny..."
                required
              />
            </FormGroup>

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Wysyłanie...' : 'Wyślij wiadomość'}
              <FiSend />
            </SubmitButton>
          </ContactForm>
        </ContactContent>
      </ContactSection>
    </PageContainer>
  )
}

export default ContactPage 